import {useEffect, useState} from "react";
import i18n from "../../i18n";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type {PaidUpgradeDefaults, SmsProvider} from "@calendall/shared";
import {colors} from "../../theme/tokens";
import {getPaidUpgradeDefaults, updatePaidUpgradeDefaults} from "../../api/paidUpgradeApi";

type BookingSettings = PaidUpgradeDefaults["bookingSettings"];

const NUMBER_SETTINGS = [
    {key: "bookingAllowedInAdvanceDays", label: "SETTINGS_BOOKING_ADVANCE_DAYS"},
    {key: "bookingAllowedInAdvanceMinMinutes", label: "SETTINGS_BOOKING_ADVANCE_MINUTES"},
    {key: "availabilityIntervalInMinutes", label: "SETTINGS_AVAILABILITY_INTERVAL"},
] as const;

const BOOLEAN_SETTINGS = [
    "showPrice",
    "showDuration",
    "allowBookingOnHolidays",
    "sendBookingConfirmationSMS",
    "customerEmailConfirmationRequired",
    "customerEmailReminder",
    "customerSMSReminder",
] as const;

const BYTES_PER_MEGABYTE = 1024 * 1024;

// An empty field means "don't change this at the upgrade", so it is omitted rather than sent as 0
function parseOptionalNumber(value: string): number | undefined {
    return value.trim() === "" ? undefined : Number(value);
}

// the backend's booking-settings constraints; slot generation advances by this interval
function isValidAvailabilityInterval(minutes: number): boolean {
    return Number.isInteger(minutes) && minutes >= 5 && minutes <= 240 && minutes % 5 === 0;
}

function withoutUndefined<T extends object>(value: T): T {
    return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}

export function PaidUpgradeDefaultsCard() {
    const {t} = useTranslation();
    const [saved, setSaved] = useState<PaidUpgradeDefaults | null>(null);
    const [email, setEmail] = useState("");
    const [storageMegabytes, setStorageMegabytes] = useState("");
    const [bookingSettings, setBookingSettings] = useState<BookingSettings>({});
    const [smsProvider, setSmsProvider] = useState<SmsProvider | "">("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    function showDefaults(defaults: PaidUpgradeDefaults) {
        setSaved(defaults);
        setEmail(defaults.quotas.email?.toString() ?? "");
        setStorageMegabytes(defaults.quotas.storageBytes === undefined ? "" : String(defaults.quotas.storageBytes / BYTES_PER_MEGABYTE));
        setBookingSettings(defaults.bookingSettings);
        setSmsProvider(defaults.smsProvider ?? "");
    }

    useEffect(() => {
        getPaidUpgradeDefaults()
                .then(showDefaults)
                .catch((e: unknown) => setError(e instanceof Error ? e.message : i18n.t("PAID_UPGRADE_DEFAULTS_LOAD_ERROR")));
    }, []);

    const storageBytes = parseOptionalNumber(storageMegabytes);
    const edited: PaidUpgradeDefaults = withoutUndefined({
        quotas: withoutUndefined({
            email: parseOptionalNumber(email),
            storageBytes: storageBytes === undefined ? undefined : Math.round(storageBytes * BYTES_PER_MEGABYTE),
        }),
        bookingSettings: withoutUndefined(bookingSettings),
        smsProvider: smsProvider === "" ? undefined : smsProvider,
    });
    const hasChanges = saved !== null && JSON.stringify(edited) !== JSON.stringify(withoutUndefined(saved));
    const invalid = [edited.quotas.email, edited.quotas.storageBytes, ...NUMBER_SETTINGS.map(({key}) => edited.bookingSettings[key])]
            .some((value) => value !== undefined && (!Number.isInteger(value) || value < 0));
    const interval = edited.bookingSettings.availabilityIntervalInMinutes;
    const invalidInterval = interval !== undefined && !isValidAvailabilityInterval(interval);

    function setBookingSetting<K extends keyof BookingSettings>(key: K, value: BookingSettings[K]) {
        setBookingSettings((settings) => ({...settings, [key]: value}));
    }

    async function save() {
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            showDefaults(await updatePaidUpgradeDefaults(edited));
            setSuccess(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : t("PAID_UPGRADE_DEFAULTS_SAVE_ERROR"));
        } finally {
            setSaving(false);
        }
    }

    return (
            <Paper variant="outlined" component="section" aria-labelledby="paid-upgrade-defaults-title" data-testid="paid-upgrade-defaults" sx={{borderTop: `3px solid ${colors.primary}`, overflow: "hidden"}}>
                <Box sx={{px: 2, py: 1.5, borderBottom: `1px solid ${colors.sectionDivider}`}}>
                    <Typography variant="h6" component="h2" id="paid-upgrade-defaults-title">{t("PAID_UPGRADE_DEFAULTS")}</Typography>
                    <Typography variant="body2" sx={{color: colors.muted}}>{t("PAID_UPGRADE_DEFAULTS_HINT")}</Typography>
                </Box>
                <Box sx={{p: 2, display: "flex", flexDirection: "column", gap: 2}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{t("PAID_UPGRADE_DEFAULTS_SAVED")}</Alert>}
                    {saved && (
                            <>
                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
                                    <TextField size="small" type="number" label={t("PAID_UPGRADE_EMAIL_TOTAL")} value={email}
                                               onChange={(e) => setEmail(e.target.value)} placeholder={t("PAID_UPGRADE_DONT_CHANGE")} />
                                    <TextField size="small" type="number" label={t("PAID_UPGRADE_STORAGE_TOTAL_MB")} value={storageMegabytes}
                                               onChange={(e) => setStorageMegabytes(e.target.value)} placeholder={t("PAID_UPGRADE_DONT_CHANGE")} />
                                    <FormControl size="small" sx={{minWidth: 200}}>
                                        <InputLabel id="paid-upgrade-sms-provider-label">{t("PAID_UPGRADE_SMS_PROVIDER")}</InputLabel>
                                        <Select labelId="paid-upgrade-sms-provider-label" label={t("PAID_UPGRADE_SMS_PROVIDER")} value={smsProvider}
                                                onChange={(e) => setSmsProvider(e.target.value as SmsProvider | "")}>
                                            <MenuItem value="">{t("PAID_UPGRADE_DONT_CHANGE")}</MenuItem>
                                            <MenuItem value="AWS_SNS">AWS SNS</MenuItem>
                                            <MenuItem value="IBASIS">IBASIS</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Typography variant="h6" component="h3">{t("SETTINGS_SECTION_BOOKING")}</Typography>
                                <Typography variant="body2" sx={{color: colors.muted}}>{t("PAID_UPGRADE_BOOKING_HINT")}</Typography>
                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
                                    {NUMBER_SETTINGS.map(({key, label}) => (
                                            <TextField key={key} size="small" type="number" label={t(label)}
                                                       value={bookingSettings[key]?.toString() ?? ""}
                                                       onChange={(e) => setBookingSetting(key, parseOptionalNumber(e.target.value))}
                                                       error={key === "availabilityIntervalInMinutes" && invalidInterval}
                                                       helperText={key === "availabilityIntervalInMinutes" && invalidInterval ? t("PAID_UPGRADE_INTERVAL_RANGE") : undefined}
                                                       placeholder={t("PAID_UPGRADE_DONT_CHANGE")} />
                                    ))}
                                    {BOOLEAN_SETTINGS.map((key) => (
                                            <FormControl key={key} size="small" sx={{minWidth: 240}}>
                                                <InputLabel id={`paid-upgrade-${key}-label`}>{t(`SETTINGS_${key}`)}</InputLabel>
                                                <Select labelId={`paid-upgrade-${key}-label`} label={t(`SETTINGS_${key}`)}
                                                        value={bookingSettings[key] === undefined ? "" : String(bookingSettings[key])}
                                                        onChange={(e) => setBookingSetting(key, e.target.value === "" ? undefined : e.target.value === "true")}>
                                                    <MenuItem value="">{t("PAID_UPGRADE_DONT_CHANGE")}</MenuItem>
                                                    <MenuItem value="true">{t("PAID_UPGRADE_ON")}</MenuItem>
                                                    <MenuItem value="false">{t("PAID_UPGRADE_OFF")}</MenuItem>
                                                </Select>
                                            </FormControl>
                                    ))}
                                </Box>
                                <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                                    <Button variant="contained" onClick={() => void save()} disabled={!hasChanges || invalid || invalidInterval || saving}>
                                        {saving ? t("DETAIL_SAVING") : t("PAID_UPGRADE_DEFAULTS_SAVE")}
                                    </Button>
                                </Box>
                            </>
                    )}
                </Box>
            </Paper>
    );
}
